import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { stripLangPrefix } from "@/i18n/language";
import type { Lang } from "@/i18n/language";
import {
  getAppPath,
  getSeo,
  buildUrl,
  buildJsonLd,
  isNoindexRoute,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  TWITTER_HANDLE,
} from "@/lib/seo";

const JSONLD_ID = "vk-seo-jsonld";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, hreflang: string | null, href: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Hält Titel, Meta-Description, Canonical, hreflang, Open Graph/Twitter sowie
 * die strukturierten Daten (schema.org) für die aktuelle Route synchron –
 * jeweils in der passenden Sprache und immer auf Basis der Produktions-Domain.
 */
export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const appPath = getAppPath();
    const lang: Lang = appPath.startsWith("/en") ? "en" : "de";
    const stripped = stripLangPrefix(appPath);
    const entry = getSeo(stripped);
    const meta = entry[lang];
    const canonical = buildUrl(appPath, lang);
    const image = entry.image || DEFAULT_OG_IMAGE;

    document.title = meta.title;
    document.documentElement.lang = lang;

    // Detailseiten (Touren, Hochzeiten, Bali-Info) erhalten den Typ "article".
    const isDetailPage =
      stripped.startsWith("/tour/") ||
      stripped.startsWith("/weddings/") ||
      stripped.startsWith("/bali-info/");

    // Grundlegende Metadaten
    upsertMeta("name", "description", meta.description);
    upsertMeta("property", "og:type", isDetailPage ? "article" : "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", meta.title);
    upsertMeta("property", "og:description", meta.description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:secure_url", image);
    upsertMeta("property", "og:image:alt", meta.title);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:locale", lang === "de" ? "de_DE" : "en_US");
    upsertMeta(
      "property",
      "og:locale:alternate",
      lang === "de" ? "en_US" : "de_DE"
    );
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:site", TWITTER_HANDLE);
    upsertMeta("name", "twitter:creator", TWITTER_HANDLE);
    upsertMeta("name", "twitter:title", meta.title);
    upsertMeta("name", "twitter:description", meta.description);
    upsertMeta("name", "twitter:image", image);
    upsertMeta("name", "twitter:image:alt", meta.title);

    // Interne Seiten und Buchungsformulare von der Indexierung ausschließen
    if (stripped.startsWith("/backstage")) {
      upsertMeta("name", "robots", "noindex, nofollow");
    } else if (isNoindexRoute(stripped)) {
      upsertMeta("name", "robots", "noindex, follow");
    } else {
      const robots = document.head.querySelector('meta[name="robots"]');
      if (robots)
        robots.setAttribute("content", "index, follow, max-image-preview:large");
    }

    // Canonical + Sprachvarianten
    upsertLink("canonical", null, canonical);
    upsertLink("alternate", "de", buildUrl(appPath, "de"));
    upsertLink("alternate", "en", buildUrl(appPath, "en"));
    upsertLink("alternate", "x-default", buildUrl(appPath, "de"));

    // Strukturierte Daten
    let script = document.getElementById(JSONLD_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = JSONLD_ID;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(buildJsonLd(appPath, lang));
  }, [location.pathname]);

  return null;
}